/**
 * Rock parts and the merged layout (plan R7, D-TBD 6). A part is one streamed cluster or background cell: an M1
 * `FieldLayout` plus a key per rock. The session keeps the active parts and merges them into `sim.field`, so M1's
 * field view and colliders (and M2's mining) work unchanged; `keys` becomes `session.fieldKeys`.
 * M3 R22i: every streamed rock's `FieldLayout.id` is `rockIdOf(key)` (stable across sessions and presets), and the
 * merged layout lists its rocks by increasing id, as M1's `fieldIndexOf` binary search needs.
 * These run on stream-ins and stream-outs (rare events), so they may allocate.
 */
import type { Vec3 } from '../core/math'
import type { FieldLayout } from '../world/field'
import { rockIdOf } from './keys'

/** One streamed piece of a field: rocks in float64 system-frame metres, with `keys[i]` naming rock i. */
export interface RockPart {
  readonly layout: FieldLayout
  readonly keys: readonly string[]
}

const ORIGIN_ROUND_M = 1000

interface MutableLayout {
  count: number
  /** M2's stable rock id: `rockIdOf(key)` for streamed rocks (M3 R22i). */
  id: Uint32Array
  origin: Vec3
  x: Float64Array
  y: Float64Array
  z: Float64Array
  radius: Float32Array
  archetype: Uint8Array
  seed: Float32Array
  spinAxis: Float32Array
  spinRate: Float32Array
}

function allocLayout(count: number, origin: Vec3): MutableLayout {
  const id = new Uint32Array(count)
  for (let i = 0; i < count; i++) id[i] = i
  return {
    count,
    id,
    origin,
    x: new Float64Array(count),
    y: new Float64Array(count),
    z: new Float64Array(count),
    radius: new Float32Array(count),
    archetype: new Uint8Array(count),
    seed: new Float32Array(count),
    spinAxis: new Float32Array(count * 3),
    spinRate: new Float32Array(count),
  }
}

function copyRock(src: FieldLayout, i: number, dst: MutableLayout, j: number): void {
  dst.id[j] = src.id[i]
  dst.x[j] = src.x[i]
  dst.y[j] = src.y[i]
  dst.z[j] = src.z[i]
  dst.radius[j] = src.radius[i]
  dst.archetype[j] = src.archetype[i]
  dst.seed[j] = src.seed[i]
  dst.spinAxis[j * 3] = src.spinAxis[i * 3]
  dst.spinAxis[j * 3 + 1] = src.spinAxis[i * 3 + 1]
  dst.spinAxis[j * 3 + 2] = src.spinAxis[i * 3 + 2]
  dst.spinRate[j] = src.spinRate[i]
}

/**
 * Sets every rock's `FieldLayout.id` to `rockIdOf(keys[i])`, in place (idempotent; M3 R22i). Returns `part`. The
 * streamer calls it on every generated part, so colliders and deltas name rocks by their stable ids.
 */
export function assignRockIds(part: RockPart): RockPart {
  const { layout, keys } = part
  for (let i = 0; i < layout.count; i++) layout.id[i] = rockIdOf(keys[i])
  return part
}

/** An empty layout at `origin`. */
export function emptyLayout(origin: Readonly<Vec3> = { x: 0, y: 0, z: 0 }): FieldLayout {
  return allocLayout(0, { x: origin.x, y: origin.y, z: origin.z })
}

/**
 * Drops the rocks whose remaining fraction in `rocks` (the system delta, rock key → fraction) is 0. Partly mined rocks
 * stay (M2 owns what a fraction does to a rock). Returns `part` itself when nothing is dropped.
 */
export function applyRockDeltas(part: RockPart, rocks: Readonly<Record<string, number>>): RockPart {
  const { layout, keys } = part
  let drop = 0
  for (let i = 0; i < layout.count; i++) if (rocks[keys[i]] === 0) drop++
  if (drop === 0) return part
  const out = allocLayout(layout.count - drop, {
    x: layout.origin.x,
    y: layout.origin.y,
    z: layout.origin.z,
  })
  const outKeys: string[] = []
  for (let i = 0; i < layout.count; i++) {
    if (rocks[keys[i]] === 0) continue
    copyRock(layout, i, out, outKeys.length)
    outKeys.push(keys[i])
  }
  return { layout: out, keys: outKeys }
}

/** The mean of the parts' origins, rounded to 1 km (the merged instancing origin); (0, 0, 0) with no parts. */
export function partsCentroid(parts: readonly RockPart[]): Vec3 {
  if (parts.length === 0) return { x: 0, y: 0, z: 0 }
  let x = 0
  let y = 0
  let z = 0
  for (const p of parts) {
    x += p.layout.origin.x
    y += p.layout.origin.y
    z += p.layout.origin.z
  }
  const n = parts.length
  return {
    x: Math.round(x / n / ORIGIN_ROUND_M) * ORIGIN_ROUND_M,
    y: Math.round(y / n / ORIGIN_ROUND_M) * ORIGIN_ROUND_M,
    z: Math.round(z / n / ORIGIN_ROUND_M) * ORIGIN_ROUND_M,
  }
}

/**
 * Merges the parts into one layout (every per-rock value copied unchanged) with their keys alongside. Each rock's id is
 * `rockIdOf(key)`, and the rocks are listed by increasing id (ties, a 32-bit collision, by key then part order), so
 * `fieldIndexOf` finds them. `origin` defaults to `partsCentroid(parts)`; the rocks keep their float64 system-frame
 * positions either way.
 */
export function mergeLayouts(parts: readonly RockPart[], origin: Readonly<Vec3> = partsCentroid(parts)): RockPart {
  let total = 0
  for (const p of parts) total += p.layout.count
  const out = allocLayout(total, { x: origin.x, y: origin.y, z: origin.z })
  const keys: string[] = new Array<string>(total)
  const ids = new Float64Array(total)
  const partOf = new Int32Array(total)
  const rockOf = new Int32Array(total)
  const order: number[] = new Array<number>(total)
  let j = 0
  for (let k = 0; k < parts.length; k++) {
    const p = parts[k]
    for (let i = 0; i < p.layout.count; i++, j++) {
      ids[j] = rockIdOf(p.keys[i])
      keys[j] = p.keys[i]
      partOf[j] = k
      rockOf[j] = i
      order[j] = j
    }
  }
  order.sort((a, b) => ids[a] - ids[b] || (keys[a] < keys[b] ? -1 : keys[a] > keys[b] ? 1 : a - b))
  const sortedKeys: string[] = new Array<string>(total)
  for (let n = 0; n < total; n++) {
    const src = order[n]
    copyRock(parts[partOf[src]].layout, rockOf[src], out, n)
    out.id[n] = ids[src]
    sortedKeys[n] = keys[src]
  }
  return { layout: out, keys: sortedKeys }
}

/** Moves every rock and the origin of a freshly generated layout by (dx, dy, dz), in place. */
export function translateLayout(layout: FieldLayout, dx: number, dy: number, dz: number): FieldLayout {
  for (let i = 0; i < layout.count; i++) {
    layout.x[i] += dx
    layout.y[i] += dy
    layout.z[i] += dz
  }
  return {
    ...layout,
    origin: {
      x: layout.origin.x + dx,
      y: layout.origin.y + dy,
      z: layout.origin.z + dz,
    },
  }
}
